import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { openAlert } from '../../Util/Util';
import Turnstile, { useTurnstile } from "react-turnstile";
import axios from 'axios';
import { submitForm as submitFormWithController } from '../../CheckFormHooks';
import Swal from 'sweetalert2';
import { useFormAttention } from '../../Hooks/useFormAttention';

const SEButtons = (props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const optionFields = useSelector(state => state.optionFields);
	const hook_status = useSelector(state => state.pageData.hook_status);
	const preview = useSelector(state => state.pageData.preview);
	const isPreview = preview === true || preview === '1' || preview === 1;
	const turnstileToken = useSelector(state => state.turnstileToken);
	const turnstileTimestamp = useSelector(state => state.turnstileTimestamp);
	const fv = useSelector(state => state.fv);
	const pageData = useSelector(state => state.pageData);

	const {formFields} = props;
	const {page, num_se, form_components} = formFields;
	const {funnel_page_id, controller} = pageData;

	const curStep = useSelector(state => state.curStep);
	const nextStepValue = useSelector(state => state.nextStep) || curStep + 1;
	
	const [indicator, setIndicator] = useState('off');
	
	// Add button ref to access button directly
	const nextButtonRef = useRef(null);
	
	// Create a ref for the Turnstile component
	const turnstileRef = useRef(null);
	
	// Make sure we can use useTurnstile hook correctly
	const turnstileHook = useTurnstile();

	// Initialize form attention hook
	const { validateBeforeNext } = useFormAttention('form-inputs', 'nextButton', 'submit-btn', { preview: isPreview, curStep, num_se });

	const progress_percent = curStep / formFields.num_se * 100;

	// Add a new selector to track jump history
	const jumpHistory = useSelector(state => state.jumpHistory) || [];

	const scrollToRegForm = () => {
		const regFormElement = document.getElementById('reg-form');
		if (regFormElement) {
			// Offset value equals the height of the top bar (52px).
			const yOffset = -52;
			const yCoordinate = regFormElement.getBoundingClientRect().top + window.scrollY + yOffset;
			window.scrollTo({ top: yCoordinate, behavior: 'smooth' });
		}
	};

	const prevStep = (e) => {
		// Prevent the default behavior and stop propagation so that other listeners don't act on this event.
		e.preventDefault();
		e.stopPropagation();

		if (curStep > 1) {
			if (jumpHistory && jumpHistory.length > 0) {
				// Find the last jump that led to current step
				const lastJump = jumpHistory.find(jump => jump.to === curStep);
				
				if (lastJump) {
					const originStep = lastJump.from;
					
					// Reset only the field that caused the jump
					const updatedOptionFields = optionFields.map(field => {
						if (field.fieldname === lastJump.fieldname) {
							// Clear DOM selection
							const formComponent = form_components.find(comp => 
								comp.fieldname === field.fieldname && 
								comp.format === 'IN'
							);
							
							if (formComponent?.input_type === 'radio') {
								$(`input[name="${field.fieldname}"]`).prop('checked', false);
							}
							
							return {
								...field,
								value: '',
								jump_to_session: null,
								jump_to_se_index: 0,
								jump_to_seq: 0
							};
						}
						return field;
					});

					// Get the next step from jump history
					let nextStepValue;
					if (jumpHistory.length > 0) {
						const latestJump = jumpHistory[jumpHistory.length - 1];
						nextStepValue = latestJump.to;
					} else {
						nextStepValue = originStep + 1;
					}

					// Update state in order, but keep the jump history
					dispatch({type: 'SET_OPTION_FIELDS', payload: updatedOptionFields});
					dispatch({type: 'SET_CUR_STEP', payload: originStep});
					dispatch({type: 'SET_NEXT_STEP', payload: nextStepValue});
				} else {
					const prev = curStep - 1;
					dispatch({type: 'SET_CUR_STEP', payload: prev});
					dispatch({type: 'SET_NEXT_STEP', payload: prev + 1});
				}
			} else {
				const prev = curStep - 1;
				dispatch({type: 'SET_CUR_STEP', payload: prev});
				dispatch({type: 'SET_NEXT_STEP', payload: prev + 1});
			}

			// Scroll to the form element
			scrollToRegForm();
		}
	};

	// Helper function to safely use the translation function
	const safeTranslate = (key, defaultValue = key) => {
		try {
			if (typeof t === 'function') {
				const result = t(key);
				// Check if translation returned the key (indicating no translation found)
				return result !== key ? result : defaultValue;
			}
			return defaultValue;
		} catch (error) {
			console.warn('Translation error:', error);
			return defaultValue;
		}
	};
	
	// Safe version of openAlert that doesn't require t to be a function
	const safeOpenAlert = (message) => {
		try {
			if (typeof openAlert === 'function') {
				// Don't pass t to avoid translation errors
				openAlert(message);
			} else if (typeof window.openAlert === 'function') {
				window.openAlert(message);
			} else if (typeof Swal !== 'undefined') {
				// Direct SweetAlert2 fallback
				Swal.fire({
					html: `<p class="text-center">${message}</p>`,
					icon: "warning",
					buttonsStyling: false,
					showCancelButton: false,
					confirmButtonText: '確定',
					returnFocus: false,
					customClass: {
						confirmButton: "btn btn-primary"
					}
				});
			} else {
				// Final fallback to browser alert
				alert(message);
			}
		} catch (error) {
			console.error('Error in safeOpenAlert:', error);
			// Ultimate fallback
			alert(message);
		}
	};

	const nextStep = (e) => {
		e.preventDefault();

		if (isPreview) {
			if (curStep === num_se) {
				return;
			}
			handleNextStep();
			return;
		}

		// Validate form attention before proceeding
		if (!validateBeforeNext(e)) {
			return; // Validation failed, stop here
		}

		// Always validate form first, even for the final step
		handleNextStep();
	};

	const advanceStep = () => {
		// New check: if any option field has jump_to_session equal to "submit_form", submit the form immediately.
		const submitField = optionFields.find(field => field.jump_to_session === "submit_form" && field.value);
		if (submitField) {
			submitForm();
			return;
		}

		if (curStep !== num_se) {
			// Check if we have any active jumps in optionFields
			const activeJump = optionFields.find(field =>
				field.jump_to_se_index === nextStepValue &&
				field.value
			);

			// If an active jump exists, record it in the jump history
			if (activeJump) {
				const newJumpHistory = [
					...jumpHistory,
					{
						from: curStep,
						to: nextStepValue,
						fieldname: activeJump.fieldname,
						value: activeJump.value,
						jump_to_session: activeJump.jump_to_session
					}
				];
				dispatch({ type: 'SET_JUMP_HISTORY', payload: newJumpHistory });
			}

			if (optionFields && optionFields.length > 0) {
				const updatedOptionFields = optionFields.map(field => ({
					...field,
					jump_to_session: field.jump_to_se_index === nextStepValue ? null : field.jump_to_session,
					jump_to_se_index: field.jump_to_se_index === nextStepValue ? 0 : field.jump_to_se_index,
					jump_to_seq: field.jump_to_se_index === nextStepValue ? 0 : field.jump_to_seq
				}));
				dispatch({ type: 'SET_OPTION_FIELDS', payload: updatedOptionFields });
			}

			dispatch({ type: 'SET_CUR_STEP', payload: nextStepValue });
			dispatch({ type: 'SET_NEXT_STEP', payload: nextStepValue + 1 });
		} else {
			// Final step - submit form
			submitForm();
		}
	};

	const handleNextStep = () => {
		if (isPreview && curStep !== num_se) {
			advanceStep();
			scrollToRegForm();
			return;
		}

		fv.validate().then(async (status) => {

			if (status === "Valid") {
				advanceStep();
			} else {
				// If validation fails, you can perform any additional error handling here.
				console.error("Form validation failed.");
			}

			// In either case (Valid or failed), scroll to the registration form.
			scrollToRegForm();
		});
	};

	// Use direct DOM event handling to prevent Tippy issues
	useEffect(() => {
		// Short delay to ensure DOM is ready
		setTimeout(() => {
			const button = document.getElementById(curStep === num_se ? 'submit-btn' : 'nextButton');
			if (button) {
				// Store for reference
				nextButtonRef.current = button;
				
				// Remove existing handler if present
				if (button._clickHandler) {
					button.removeEventListener('click', button._clickHandler, true);
				}
				
				// Create a handler that will call our nextStep function
				const handleClick = (e) => {
					// Prevent default before doing anything else
					e.preventDefault();
					e.stopPropagation();
					
					
					// Check for stale button state at each click
					if (button.getAttribute('data-processing') === 'true' && indicator === 'off') {
						resetButtonState(button);
					}
					
					// Only check processing state if this is the final step (form submission)
					if (curStep === num_se) {
						// Final step - check if button is processing before submission
						if (button.getAttribute('data-processing') === 'true' || 
							button.disabled || 
							hook_status !== 'live' || 
							isPreview) {
							return;
						}
						
						// Set processing state for form submission
						button.setAttribute('data-processing', 'true');
					} else {
						// Navigation step - block only when not live and not preview
						if (hook_status !== 'live' && !isPreview) {
							return;
						}

						// Don't set processing attribute for navigation between steps
						// This allows multiple clicks for navigation
					}
					
					nextStep(e);
				};
				
				// Store handler on button for cleanup
				button._clickHandler = handleClick;
				
				// Add with capture to ensure we get it first
				button.addEventListener('click', handleClick, true);
				
			}
		}, 300);
		
		// Cleanup
		return () => {
			const button = document.getElementById(curStep === num_se ? 'submit-btn' : 'nextButton');
			if (button && button._clickHandler) {
				button.removeEventListener('click', button._clickHandler, true);
				delete button._clickHandler;
			}
		};
	}, [curStep, nextStep, hook_status, preview, num_se, turnstileToken, indicator]);

	/**
	 * Extract Turnstile token from various sources
	 * Priority: 1) Redux state, 2) window.turnstileToken, 3) hidden input
	 * @param {string|object} turnstileTokenValue - Token from Redux state
	 * @returns {string|null} Turnstile token or null if not found
	 */
	const extractTurnstileToken = (turnstileTokenValue) => {
		// Case 1: turnstileTokenValue is directly a string
		if (typeof turnstileTokenValue === 'string' && turnstileTokenValue.length > 10) {
			return turnstileTokenValue;
		}
		
		// Case 2: turnstileTokenValue is an object with response property (standard Turnstile format)
		if (turnstileTokenValue && typeof turnstileTokenValue === 'object') {
			if (turnstileTokenValue.response) {
				return turnstileTokenValue.response;
			}
			
			// Case 3: turnstileTokenValue is an object with token property
			if (turnstileTokenValue.token) {
				return turnstileTokenValue.token;
			}
		}
		
		// Case 4: Check window global
		if (window.turnstileToken && typeof window.turnstileToken === 'string') {
			return window.turnstileToken;
		}
		
		// Case 5: Check form for hidden inputs
		const form = document.getElementById('reg-form');
		if (form) {
			// Check standard turnstile field names
			const tokenFieldNames = [
				'cf-turnstile-response', 
				'turnstile-response', 
				'cf-turnstile-token',
				'g-recaptcha-response'
			];
			
			for (const fieldName of tokenFieldNames) {
				const field = form.querySelector(`input[name="${fieldName}"]`);
				if (field && field.value && field.value.length > 10) {
					return field.value;
				}
			}
			
			// Check form dataset
			if (form.dataset && form.dataset.turnstileToken) {
				return form.dataset.turnstileToken;
			}
		}
		
		// Case 6: Try to get from turnstileHook
		if (turnstileHook && typeof turnstileHook.getResponse === 'function') {
			try {
				const hookResponse = turnstileHook.getResponse();
				if (hookResponse) {
					return hookResponse;
				}
			} catch (e) {
				console.error('Error getting token from turnstileHook:', e);
			}
		}
		
		return null;
	};
	
	// Update the submitForm function to integrate with CheckFormHooks directly
	const submitForm = event => {
		if (event && event.preventDefault) {
			event.preventDefault();
		}
		setIndicator('on');
		const form = document.getElementById('reg-form');
		const submitButton = document.getElementById(curStep === num_se ? 'submit-btn' : 'nextButton');
		if (!form || !submitButton) {
			console.error('Form or submit button not found');
			setIndicator('off');
			return;
		}
		const needsTurnstile = Boolean(pageData.turnstile_required || window.__form_turnstile_required__);
		const ajaxUrl = `/${controller}/ajax-check-form/${funnel_page_id}`;
		const runSubmission = (submitToken) => {
			const options = {
				controller,
				funnel_page_id,
				turnstileToken: submitToken || undefined,
				t: t,
				openAlert: safeOpenAlert,
				ajaxUrl,
				turnstileRequired: needsTurnstile
			};
			return submitFormWithController(submitButton, form, options)
				.then(result => {
					if (!result.success) {
						if (result.require_turnstile && typeof window.__set_turnstile_required__ === 'function') {
							window.__set_turnstile_required__();
						}
						resetButtonState(submitButton);
					}
				})
				.catch(error => {
					console.error('Form submission error:', error);
					resetButtonState(submitButton);
				});
		};
		if (!needsTurnstile) {
			runSubmission(null);
			return;
		}
		if (turnstileHook && typeof turnstileHook.getResponse === 'function') {
			try {
				const response = turnstileHook.getResponse();
				if (response) {
					window.turnstileToken = response;
				}
			} catch (e) {
				console.error('Error getting response from turnstileHook:', e);
			}
		}
		const token = extractTurnstileToken(turnstileToken);
		if (!token && typeof window.__ensure_turnstile_token__ === 'function') {
			window.__ensure_turnstile_token__().then((freshToken) => {
				if (freshToken) {
					runSubmission(freshToken);
					return;
				}
				resetButtonState(submitButton);
				safeOpenAlert(safeTranslate('Please verify you are not a robot'));
			}).catch(() => {
				resetButtonState(submitButton);
				safeOpenAlert(safeTranslate('Please verify you are not a robot'));
			});
			return;
		}
		if (!token && turnstileHook && typeof turnstileHook.execute === 'function') {
			try {
				turnstileHook.execute();
				setTimeout(() => {
					const newToken = extractTurnstileToken(turnstileToken) || window.turnstileToken;
					if (newToken) {
						runSubmission(newToken);
						return;
					}
					resetButtonState(submitButton);
					safeOpenAlert(safeTranslate('Please verify you are not a robot'));
				}, 1000);
				return;
			} catch (e) {
				console.error('Error executing Turnstile via hook:', e);
				resetButtonState(submitButton);
				return;
			}
		}
		if (token) {
			runSubmission(token);
			return;
		}
		resetButtonState(submitButton);
		safeOpenAlert(safeTranslate('Please verify you are not a robot'));
	};
	
	// Helper function to reset button state after errors
	const resetButtonState = (button) => {
		if (button) {
			
			// Ensure we're accessing the DOM element directly
			const buttonElement = typeof button === 'string' ? document.getElementById(button) : button;
			
			if (buttonElement) {
				// Force reset all state attributes
				buttonElement.setAttribute('data-processing', 'false');
				buttonElement.removeAttribute('disabled');
				buttonElement.disabled = false;
				buttonElement.classList.remove('disabled');
				
				// Reset the indicator state in React
				setIndicator('off');
				
			} else {
				console.error('Button element not found in resetButtonState');
			}
		} else {
			console.error('No button provided to resetButtonState');
			
			// Fallback to find button by ID if no button provided
			const nextButton = document.getElementById(curStep === num_se ? 'submit-btn' : 'nextButton');
			if (nextButton) {
				nextButton.setAttribute('data-processing', 'false');
				nextButton.removeAttribute('disabled');
				nextButton.disabled = false;
				nextButton.classList.remove('disabled');
				setIndicator('off');
			}
		}
	};

	var tippyContent = '';
	switch (hook_status) {
		case 'warmup':
			tippyContent = t('活動即將開始');
			break;

		case 'expired':
			tippyContent = t('活動已結束');
			break;
	}

	// Add a reliable openAlert function if not already available
	useEffect(() => {
		// Create or override global openAlert that properly handles translations
		window.openAlert = (message, translationFunc) => {
			
			// Check if translationFunc is a function and use it if it is
			let displayMessage = message;
			if (typeof translationFunc === 'function') {
				try {
					displayMessage = translationFunc(message);
				} catch (e) {
					console.error('Error using translation function:', e);
					// On error, fall back to using the original message
					displayMessage = message;
				}
			}
			
			// Use Swal if available, otherwise fallback to native alert
			if (typeof Swal !== 'undefined') {
				Swal.fire({
					title: '',
					text: displayMessage,
					icon: 'warning',
					confirmButtonText: 'OK'
				});
			} else {
				// Display native alert as fallback
				alert(displayMessage);
			}
		};
		
		return () => {
			// Don't remove the function on unmount to avoid breaking other components
		};
	}, []);

	return (
		<div className="survey-toolbar  d-flex px-3 align-items-center reset">
			{curStep > 1 && (
				<label
					className="btn btn-secondary form-btn"
					id="prevButton"
					onClick={prevStep}
				>
					{t('上一步')}
				</label>
			)}
			<div className="form-progress flex-grow-1 px-3">
		    	<div className="col ">
		        	<p className="text-gray-700 fz12 mb-0">{t('填寫進度：')}<span id="current-step" className="fw-bolder">{curStep}</span>/<span id="total-steps">{num_se}</span></p>
		    	</div>
		    
		    	<div className="progress mt-1 mw-200px mx-auto">
		        	<div
		        		id="progress-bar"
		        		className="progress-bar"
		        		role="progressbar"
		        		style={{width: `${progress_percent}%`}}
		        		aria-valuenow={curStep}
		        		aria-valuemin="0"
		        		aria-valuemax={num_se}
		        	/>
		    	</div>
		    </div>
		    <Tippy
		    	theme="primary"
		    	content={tippyContent}
		    	disabled={tippyContent === ''}
		    >
			    <span>
				    <button
				    	type={curStep === num_se ? 'submit' : 'button'}
				    	id={curStep === num_se ? 'submit-btn' : 'nextButton'}
				    	className="btn btn-primary form-btn"
				    	data-kt-indicator={indicator}
				    	onClick={nextStep}
				    	disabled={isPreview ? (indicator === 'on' || curStep === num_se) : (hook_status !== 'live' || indicator === 'on')}
				    >
				    	<span className="indicator-label">{curStep === num_se ? page.cta_text : t('下一步')}</span>
				    	<span className="indicator-progress">
				    		{t('請稍候...')}<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
				    	</span>
				    </button>
			    </span>
			 </Tippy>
		</div>
	);
}

export default SEButtons;